import { useContext, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import {
  GET_ACTIVE_USER,
  LOGIN,
  REQUEST_PASSWORD_RESET,
  CREATE_ADDRESS,
} from "../../../queries/auth.queries";
import { GET_ACTIVE_ORDER, GET_TOTAL_LINES } from "../../../queries/cart.queries";
import { SET_SHIPPING_ORDER } from "../../../queries/checkout.queries";
import {
  LoginResponse,
  LoginVariables,
  RequestPasswordResetResponse,
  RequestPasswordResetVariables,
} from "../../../queries/auth.queries.types";
import { AccountContext } from "../../../context/account.context";
import { useAuthErrorAlert } from "../useAuthErrorAlert";
import { ForgotPasswordFormValues, LoginFormValues } from "./LoginForm.types";

export const useLoginForm = (handleClose: () => void, route: string) => {
  const history = useHistory();
  const showError = useAuthErrorAlert();

  const { accountState, setAccountState } = useContext(
    AccountContext
  ) as unknown as {
    accountState: Record<string, unknown>;
    setAccountState: (state: Record<string, unknown>) => void;
  };

  const [forgotMode, setForgotMode] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [setShippingAddressMutation, { error: setOrderShippingMutationError }] =
    useMutation(SET_SHIPPING_ORDER, {
      refetchQueries: [{ query: GET_ACTIVE_USER }, { query: GET_ACTIVE_ORDER }],
      awaitRefetchQueries: true,
      onCompleted: (data: any) => {
        if (data?.setOrderShippingAddress?.message) {
          showError(data.setOrderShippingAddress.message);
        }
      },
    });

  const [createAddress] = useMutation(CREATE_ADDRESS, {
    onCompleted: async () => {
      const shippingAddress = sessionStorage.getItem("shippingAddress");
      if (!shippingAddress) return;
      const addressValues = JSON.parse(shippingAddress);

      await setShippingAddressMutation({
        variables: {
          fullName: addressValues.fullName,
          streetLine1: addressValues.streetLine1,
          streetLine2: null,
          city: addressValues.city,
          postalCode: addressValues.postalCode,
          countryCode: "IL",
          phoneNumber: addressValues.phoneNumber,
          defaultShippingAddress: true,
          defaultBillingAddress: true,
        },
      });
    },
  });

  const [login] = useMutation<LoginResponse, LoginVariables>(LOGIN, {
    refetchQueries: [{ query: GET_ACTIVE_USER }, { query: GET_TOTAL_LINES }],
    awaitRefetchQueries: true,
  });

  const [requestReset] = useMutation<
    RequestPasswordResetResponse,
    RequestPasswordResetVariables
  >(REQUEST_PASSWORD_RESET);

  useEffect(() => {
    if (!setOrderShippingMutationError?.message) return;
    showError(setOrderShippingMutationError.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOrderShippingMutationError]);

  const redirectUser = () => {
    history.push(route);

    const isCheckoutPage = window.location.href.includes("checkout");
    if (isCheckoutPage) {
      setAccountState({ ...accountState, isUser: true });
      return;
    }

    window.location.reload();
  };

  const onSubmit = async (values: LoginFormValues) => {
    try {
      const res = await login({
        variables: {
          username: values.email,
          password: values.password,
          rememberMe: false,
        },
      });
      const loginResult = res.data?.login;

      if (loginResult?.__typename !== "CurrentUser") {
        showError(loginResult?.message);
        return;
      }

      const shippingAddress = sessionStorage.getItem("shippingAddress");
      if (shippingAddress) {
        const addressValues = JSON.parse(shippingAddress);

        await createAddress({
          variables: {
            fullName: "",
            streetLine1: addressValues.streetLine1,
            countryCode: "IL",
            phoneNumber: "",
            defaultShippingAddress: true,
            defaultBillingAddress: true,
            streetLine2: "",
            city: "",
            province: "",
            postalCode: "",
          },
        });
      }

      setAccountState({ ...accountState, isUser: true });
      handleClose();
      redirectUser();
    } catch (err) {
      showError((err as Error)?.message);
    }
  };

  const onForgotSubmit = async (values: ForgotPasswordFormValues) => {
    try {
      const res = await requestReset({
        variables: { emailAddress: values.email },
      });
      const result = res.data?.requestPasswordReset;

      if (result?.__typename !== "Success") {
        showError(result?.message);
        return;
      }

      setEmailSent(true);
    } catch (err) {
      showError((err as Error)?.message);
    }
  };

  const enterForgotMode = () => setForgotMode(true);

  return {
    forgotMode,
    emailSent,
    onSubmit,
    onForgotSubmit,
    enterForgotMode,
  };
};
